import { RouteAuthenticationRequirement, RouteItem } from '@invara-dev/react-base';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AppRoles } from '../constants/appRoles';
import { Layout } from '../components/layout/layout.tsx';
import { ApplicationRoutes } from '../constants/appRoutes';
import {
  createFlaggedLegacyRoute,
  createForcedLegacyLeaf,
  createForcedLegacyRoute,
  createLegacyLeaf,
  createLegacyRoute
} from './legacyRouteHelpers.tsx';
import { FeatureFlags } from '../constants/featureFlags.ts';
import {
  V2InvoiceCreateRouteItem,
  V2InvoicingHiddenRoutes,
  v2InvoicingRoles
} from '../features/v2/invoicing/InvoicingRoutes.tsx';
import { editJobLegalRoles } from './legacyRouteRolePolicies.ts';

// --- Shared role arrays used across categories ---
const timesheetRoles = [
  AppRoles.AdminJr,
  AppRoles.Admin,
  AppRoles.Checker,
  AppRoles.Drafter,
  AppRoles.ProjectManager,
  AppRoles.Manager,
  AppRoles.PartyChief,
  AppRoles.SurveyAssistant
];
const managementRoles = [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager, AppRoles.QuickLinkManagement];
const adminOnly = [AppRoles.Admin];
const adminAndJr = [AppRoles.Admin, AppRoles.AdminJr];

const reportViewerRoles = [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager];
const municipalReportingRoles = [...reportViewerRoles, AppRoles.MunicipalOfficeReporting];
const companyCrmRoles = [
  AppRoles.Admin,
  AppRoles.AdminJr,
  AppRoles.ProjectManager,
  AppRoles.Manager,
  AppRoles.QuickLinkManagement
];
const fieldTimeApprovalRoles = [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager, AppRoles.FieldTimeApprover];
const officeTimeApprovalRoles = [AppRoles.Admin, AppRoles.OfficeTimeApprover, AppRoles.SupervisorOfficeTimeApprover];
const invoiceApprovalRoles = [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.AdminJr];
const legacyInvoiceCreationRoles = [
  AppRoles.Admin,
  AppRoles.AdminJr,
  AppRoles.ProjectManager,
  AppRoles.Manager,
  AppRoles.PartyChief
];
const invoicingNavRoles = Array.from(
  new Set([
    AppRoles.Admin,
    AppRoles.AccountingAdmin,
    AppRoles.VendorAdmin,
    AppRoles.AdminJr,
    ...legacyInvoiceCreationRoles
  ])
);
const userManagementRoles = [AppRoles.Admin, AppRoles.AdminJr, AppRoles.UserAdmin];
const editJobRoles = [
  AppRoles.AdminJr,
  AppRoles.Admin,
  AppRoles.Checker,
  AppRoles.Drafter,
  AppRoles.ProjectManager,
  AppRoles.Manager,
  AppRoles.PartyChief,
  AppRoles.SurveyAssistant,
  AppRoles.VendorAdmin
];

const LegacyEditJobRedirect = () => {
  const location = useLocation();

  return <Navigate to={`/legacy/editjob${location.search}${location.hash}`} replace />;
};

const quickLinksRouteItem: RouteItem = {
  text: 'Quick Links',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.quickLinks.overview,
  allowedRoles: [],
  layout: <Layout />,
  async lazy() {
    const component = await import('../features/v2/quickLinks/QuickLinksPage.tsx');
    return { Component: component.default };
  }
};

const quickLinkManagementRouteItem: RouteItem = {
  text: 'Quick Link Management',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.quickLinks.management,
  allowedRoles: [AppRoles.Admin, AppRoles.QuickLinkManagement],
  layout: <Layout />,
  async lazy() {
    const component = await import('../features/v2/quickLinks/QuickLinkManagementPage.tsx');
    return { Component: component.default };
  }
};

const mailingListManagementRouteItem: RouteItem = {
  text: 'Mailing List Management',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.admin.mailingListManagement,
  allowedRoles: adminOnly,
  layout: <Layout />,
  async lazy() {
    const component = await import('../features/v2/admin/mailingLists/MailingListManagementPage.tsx');
    return { Component: component.default };
  }
};

const dropdownApplicationManagementRouteItem: RouteItem = {
  text: 'DropDown/Application Management',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.admin.dropdownApplicationManagement,
  allowedRoles: adminOnly,
  layout: <Layout />,
  async lazy() {
    const component = await import('../features/v2/admin/dropdownApplication/DropdownApplicationManagementPage.tsx');
    return { Component: component.default };
  }
};

const jobStatusReportRouteItem: RouteItem = {
  text: 'Job Status Report',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: '/legacy/management/job-status',
  allowedRoles: [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager],
  async lazy() {
    const component = await import('../features/v2/reporting/jobStatusReport/JobStatusReportPage.tsx');
    return { Component: component.default };
  }
};

const hrManagementRouteItem: RouteItem = {
  text: 'HR',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.admin.hrManagement,
  allowedRoles: [],
  layout: <Layout />,
  async lazy() {
    const component = await import('../features/hrManagement/overview/HrManagement.tsx');
    return { Component: component.default };
  }
};

const rateSheetManagementV2RouteItem: RouteItem = {
  text: 'Ratesheet Management',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.invoicing.rateSheetManagement,
  allowedRoles: v2InvoicingRoles,
  async lazy() {
    const component = await import('../features/v2/rateSheets/overview/RateSheetManagement.tsx');
    return { Component: component.default };
  }
};

const invoiceRulesEngineV2RouteItem: RouteItem = {
  text: 'Invoice Rules Engine',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.invoicing.rulesEngineV2,
  allowedRoles: v2InvoicingRoles,
  async lazy() {
    const m = await import('../features/v2/invoicing/rulesEngine/InvoiceRulesEngineV2Page.tsx');
    return { Component: m.default };
  }
};

const viewInvoicesV2RouteItem: RouteItem = {
  text: 'View Invoice',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.invoicing.invoiceList,
  allowedRoles: v2InvoicingRoles,
  async lazy() {
    const m = await import('../features/v2/invoicing/viewInvoices/ViewInvoicesPage.tsx');
    return { Component: m.default };
  }
};

const accountCodeMappingV2RouteItem: RouteItem = {
  text: 'Account Code Mapping',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.invoicing.accountCodeMapping,
  allowedRoles: v2InvoicingRoles,
  async lazy() {
    const m = await import('../features/v2/invoicing/accountCodeMapping/AccountCodeMappingPage.tsx');
    return { Component: m.default };
  }
};

const accountCodeManagementV2RouteItem: RouteItem = {
  text: 'Account Code Management',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.invoicing.accountCodeManagement,
  allowedRoles: v2InvoicingRoles,
  async lazy() {
    const m = await import('../features/v2/invoicing/accountCodeManagement/AccountCodeManagementPage.tsx');
    return { Component: m.default };
  }
};

/**
 * Main navigation items in the requested order:
 * Job Book, Timesheet, Purchases, Management, Inventory, Safety, Payroll, Invoicing, Invoicing V2, Field Tools,
 * Quick Links, Admin.
 */
export function getLegacyNavItems(isHr: boolean): RouteItem[] {
  return [
    // 1. Job Book (React tabbed workspace when `NewJobBook` flag is enabled,
    //    legacy iframe fallback otherwise)
    createFlaggedLegacyRoute({
      text: 'Job Book',
      path: ApplicationRoutes.jobs.overview,
      legacyPath: '/JobBook/Index',
      allowedRoles: [
        AppRoles.AdminJr,
        AppRoles.Admin,
        AppRoles.Checker,
        AppRoles.Drafter,
        AppRoles.ProjectManager,
        AppRoles.Manager,
        AppRoles.PartyChief,
        AppRoles.SurveyAssistant,
        AppRoles.VendorAdmin
      ],
      flagKey: FeatureFlags.NewJobBook,
      loadNewComponent: () => import('../features/v2/jobs/overview/JobBookManagement.tsx')
    }),

    // 2. Timesheet (dropdown) – user list first, then leftovers at bottom
    {
      text: 'Timesheet',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: timesheetRoles,
      layout: <Layout />,
      subItems: [
        createLegacyLeaf('My Time Off Requests', '/legacy/time-off', '/TimeOffRequests/Index', timesheetRoles, true),
        createLegacyLeaf(
          'My Safety Report Tickets',
          '/legacy/timesheet/safety',
          '/DailySafetyTickets/Index',
          [AppRoles.PartyChief, AppRoles.Admin, AppRoles.SafetyManagement],
          true
        ),
        createLegacyLeaf(
          'Request Bank Time Payout',
          '/legacy/time-off/request-bank-time-payout',
          '/BankTime/RequestBankTimePayout',
          [AppRoles.BankedTime],
          true
        ),
        // Leftovers at bottom
        createLegacyLeaf('Timesheet', '/legacy/timesheet', '/TimesheetNew/Index', timesheetRoles, true),
        createLegacyLeaf(
          'Monthly Timesheet Invoice',
          '/legacy/timesheet/invoice',
          '/TimesheetNew/TimesheetInvoice',
          [AppRoles.Admin, AppRoles.PartyChief],
          true
        )
      ]
    },

    // 4. Purchases
    {
      text: 'Purchases',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [
        AppRoles.AdminJr,
        AppRoles.Admin,
        AppRoles.PartyChief,
        AppRoles.PurchaseOrderOnly,
        AppRoles.Manager
      ],
      layout: <Layout />,
      subItems: [
        {
          text: 'Purchase Orders',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.purchasing.purchaseOrders,
          allowedRoles: [
            AppRoles.AdminJr,
            AppRoles.Admin,
            AppRoles.PartyChief,
            AppRoles.PurchaseOrderOnly,
            AppRoles.Manager
          ],
          async lazy() {
            const component = await import('../features/v2/purchasing/purchaseOrderReport/PurchaseOrderReportPage.tsx');
            return { Component: component.default };
          }
        },
        createLegacyLeaf(
          'Vendor Purchases',
          '/legacy/accounting/vendor-purchases',
          '/Accounting/VendorPurchases',
          [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.AdminJr],
          true
        ),
        createLegacyLeaf(
          'Accommodations Report',
          '/legacy/reporting/accommodations',
          '/Reporting/AccomodationsReport',
          [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
          true
        )
      ]
    },

    // 5. Management
    {
      text: 'Management',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: managementRoles,
      layout: <Layout />,
      subItems: [
        {
          text: 'Company CRM',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.admin.companyManagement,
          allowedRoles: companyCrmRoles,
          async lazy() {
            const component = await import('../features/v2/company/overview/CompanyManagement.tsx');
            return { Component: component.default };
          }
        },
        createLegacyLeaf(
          'Crew Tracking',
          '/legacy/management/crew-tracking',
          '/CrewTracking/Index',
          [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager],
          true
        ),
        jobStatusReportRouteItem,
        quickLinkManagementRouteItem,
        createLegacyLeaf(
          'Custom Job Code Management',
          '/legacy/admin/custom-job-codes',
          '/CustomJobManagement/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'Custom Job Management',
          '/legacy/admin/custom-jobs',
          '/CustomJobManagement/JobManagement',
          adminOnly,
          true
        ),
        dropdownApplicationManagementRouteItem,
        createLegacyLeaf('Job Locks', '/legacy/admin/job-locks', '/Admin/EditLock/Index', adminOnly, true),
        mailingListManagementRouteItem,
        createLegacyLeaf(
          'Email Group Management',
          '/legacy/admin/email-groups',
          '/Admin/EmailGroups/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'Report Builder Management',
          '/legacy/admin/report-builder',
          '/ReportBuilder/Manage',
          [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
          true
        ),
        createLegacyLeaf(
          'Job Report Builder',
          '/legacy/reporting/report-builder',
          '/ReportBuilder/Index',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'Production Status Report',
          '/legacy/reporting/job-production',
          '/ProductionStatus/Index',
          [AppRoles.Admin, AppRoles.ProductionStatusReport],
          true
        ),
        createLegacyLeaf(
          'Legal Status Report',
          '/legacy/reporting/legal-status',
          '/ProductionStatus/LegalStatusReport',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'Municipal Field Work Listings',
          '/legacy/reporting/municipal-field-work',
          '/Reporting/MunicipalJobsReport?field=true&misc=false',
          municipalReportingRoles,
          true
        ),
        createLegacyLeaf(
          'Municipal Misc Office Report',
          '/legacy/reporting/municipal-misc-office',
          '/Reporting/MunicipalJobsReport?field=false&misc=true',
          municipalReportingRoles,
          true
        )
      ]
    },

    ...(isHr ? [hrManagementRouteItem] : []),

    // 6. Inventory
    {
      text: 'Inventory',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [
        AppRoles.Admin,
        AppRoles.AdminJr,
        AppRoles.AccountingAdmin,
        AppRoles.OfficeEquipmentManagement,
        AppRoles.Manager
      ],
      layout: <Layout />,
      subItems: [
        {
          text: 'Equipment Management',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.admin.equipmentManagement,
          allowedRoles: [
            AppRoles.Admin,
            AppRoles.AdminJr,
            AppRoles.AccountingAdmin,
            AppRoles.OfficeEquipmentManagement
          ],
          async lazy() {
            const m = await import('../features/v2/equipment/management/EquipmentManagementPage.tsx');
            return { Component: m.default };
          }
        },
        {
          text: 'Equipment History Report',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.admin.equipmentHistoryReport,
          allowedRoles: [AppRoles.Admin, AppRoles.AdminJr, AppRoles.AccountingAdmin],
          async lazy() {
            const m = await import('../features/v2/equipment/history/EquipmentHistoryReportPage.tsx');
            return { Component: m.default };
          }
        },
        createLegacyLeaf(
          'Equipment Owner Report',
          '/legacy/reporting/equipment-owner',
          '/Equipment/EquipmentOwnerReport?isFieldEquipment=true',
          [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
          true
        )
      ]
    },

    // 7. Safety
    {
      text: 'Safety',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager, AppRoles.SafetyManagement],
      layout: <Layout />,
      subItems: [
        createLegacyLeaf(
          'Safety Ticket Management',
          '/legacy/admin/safety-tickets',
          '/Admin/DropDown/SafetyTicketManagement',
          [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager],
          true
        ),
        createLegacyLeaf(
          'Daily Safety Report Tickets',
          '/legacy/reporting/daily-safety',
          '/DailySafetyTickets/Index',
          [AppRoles.Admin, AppRoles.SafetyManagement],
          true
        ),
        createLegacyLeaf(
          'Job KM Usage Report',
          '/legacy/reporting/job-km',
          '/EquipmentUsageReport/JobKmReport',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'My Safety Report Tickets',
          '/legacy/timesheet/safety',
          '/DailySafetyTickets/Index',
          [AppRoles.PartyChief, AppRoles.Admin, AppRoles.SafetyManagement],
          true
        ),
        {
          text: 'Safety Ticket Report',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.safety.safetyTicketReport,
          allowedRoles: reportViewerRoles,
          async lazy() {
            const m = await import('../features/v2/safety/safetyTicketReport/SafetyTicketReportPage.tsx');
            return { Component: m.default };
          }
        },
        createLegacyLeaf(
          'ISNetworld Safety Report',
          '/legacy/reporting/isnetworld',
          '/Reporting/IsNetworldSafetyReport',
          [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
          true
        )
      ]
    },

    // 8. Payroll
    {
      text: 'Payroll',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [
        AppRoles.Admin,
        AppRoles.AdminJr,
        AppRoles.Manager,
        AppRoles.ProjectManager,
        AppRoles.OfficeTimeApprover,
        AppRoles.FieldTimeApprover
      ],
      layout: <Layout />,
      subItems: [
        createLegacyLeaf(
          'Field Time Approvals',
          '/legacy/admin/field-time-approvals',
          '/TimesheetApproval/Index?isOfficeTime=false',
          fieldTimeApprovalRoles,
          true
        ),
        createLegacyLeaf(
          'Office Time Approvals',
          '/legacy/admin/office-time-approvals',
          '/TimesheetApproval/Index?isOfficeTime=true',
          officeTimeApprovalRoles,
          true
        ),
        createLegacyLeaf(
          'Time Off Approvals',
          '/legacy/admin/time-off-approvals',
          '/TimeOffApproval/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf('Stat Holidays', '/legacy/admin/holidays', '/Admin/HolidayManagement/Index', adminOnly, true),
        createLegacyLeaf(
          'Biweekly Payroll Report',
          '/legacy/reporting/biweekly-payroll',
          '/Reporting/PayrollReport',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'Monthly Payroll Report',
          '/legacy/reporting/monthly-payroll',
          '/Reporting/MonthlyPayrollReport',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'Production Payroll Report',
          '/legacy/reporting/production-payroll',
          '/TimesheetNew/ProductionPayrollReport',
          adminOnly,
          true
        ),
        {
          text: 'Timesheet Summary',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.reporting.timesheetSummary,
          allowedRoles: reportViewerRoles,
          async lazy() {
            const m = await import('../features/v2/reporting/timesheetSummary/TimesheetSummaryReportPage.tsx');
            return { Component: m.default };
          }
        },
        {
          text: 'Timesheet Summary (Field)',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.reporting.timesheetSummaryField,
          allowedRoles: managementRoles,
          async lazy() {
            const m = await import('../features/v2/reporting/timesheetSummary/TimesheetSummaryReportPage.tsx');
            const TimesheetSummaryReportPage = m.default;
            return { Component: () => <TimesheetSummaryReportPage fieldTimeOnly={true} /> };
          }
        },
        createLegacyLeaf(
          'Equipment Usage Report',
          '/legacy/reporting/equipment-usage',
          '/EquipmentUsageReport/Index',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'Equipment Usage Charge Rates',
          '/legacy/accounting/equipment-rates',
          '/EquipmentRates/EquipmentUsageChargeRates',
          [AppRoles.Admin, AppRoles.AccountingAdmin],
          true
        ),
        createLegacyLeaf(
          'Employee Report',
          '/legacy/reporting/user-report',
          '/Admin/UserManagement/UserManagementReport',
          reportViewerRoles,
          true
        )
      ]
    },

    // 9. Invoicing
    {
      text: 'Invoicing',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: invoicingNavRoles,
      layout: <Layout />,
      subItems: [
        {
          text: 'Account Code Management',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.invoicing.invoiceCodeManagement,
          allowedRoles: [AppRoles.AccountingAdmin],
          async lazy() {
            const m = await import('../features/v2/invoicing/invoiceCodeManagement/InvoiceCodeManagementPage.tsx');
            return { Component: m.default };
          }
        },
        createForcedLegacyLeaf(
          'Approved Invoice Report',
          '/legacy/accounting/approved-invoices',
          '/InvoiceReport/ApprovedInvoiceReport',
          [AppRoles.Admin, AppRoles.AccountingAdmin],
          true
        ),
        createLegacyLeaf(
          'Change Compass Address',
          '/legacy/accounting/address',
          '/CompassAddress/Index',
          [AppRoles.Admin, AppRoles.AccountingAdmin],
          true
        ),
        createLegacyLeaf(
          'Invoice Report',
          '/legacy/reporting/invoice-report',
          '/InvoiceReport/Index',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'Daily Field Ratesheet Management',
          '/legacy/admin/daily-field-rates',
          '/DailyFieldRateManagement/Index',
          adminOnly,
          true
        ),
        createForcedLegacyLeaf(
          'Job Ratesheet Management',
          '/legacy/admin/job-ratesheets',
          '/Admin/RateSheetManagement/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'Invoice Rules Engine',
          '/legacy/admin/invoice-rules',
          '/InvoiceRulesEngine/Index',
          adminOnly,
          true
        ),
        createFlaggedLegacyRoute({
          text: 'Invoice Approval',
          path: ApplicationRoutes.invoicing.invoiceApproval,
          legacyPath: '/Accounting/Invoices',
          allowedRoles: invoiceApprovalRoles,
          flagKey: FeatureFlags.UseLegacyInvoiceApproval,
          loadNewComponent: () => import('../features/v2/invoicing/invoiceApproval/InvoiceApprovalPage.tsx'),
          isNewApp: true,
          serveLegacyWhenEnabled: true
        }),
        {
          text: 'Invoice Number Report',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.invoicing.invoiceNumberReport,
          allowedRoles: [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.VendorAdmin],
          async lazy() {
            const m = await import('../features/v2/invoicing/invoiceNumberReport/InvoiceNumberReportPage.tsx');
            return { Component: m.default };
          }
        },
        createLegacyLeaf(
          'Charge Out Ratio Report',
          '/legacy/accounting/charge-out',
          '/AdminReport/ChargeOutRatioReport',
          [AppRoles.AccountingAdmin],
          true
        ),
        createLegacyLeaf(
          'Sales Report',
          '/legacy/accounting/sales',
          '/Reporting/SalesReport',
          [AppRoles.Admin, AppRoles.AccountingAdmin],
          true
        )
      ]
    },

    // 10. Invoicing V2
    {
      text: 'Invoicing V2',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: v2InvoicingRoles,
      layout: <Layout />,
      subItems: [
        viewInvoicesV2RouteItem,
        invoiceRulesEngineV2RouteItem,
        rateSheetManagementV2RouteItem,
        accountCodeMappingV2RouteItem,
        accountCodeManagementV2RouteItem
      ]
    },

    // 11. Field Tools – user list first, then leftovers at bottom
    {
      text: 'Field Tools',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [AppRoles.Admin, AppRoles.Manager, AppRoles.PartyChief, AppRoles.Gis],
      layout: <Layout />,
      subItems: [
        createLegacyLeaf(
          'Survey Points',
          '/legacy/survey-points',
          '/SurveyPoint/Index',
          [AppRoles.Admin, AppRoles.Manager, AppRoles.PartyChief],
          true
        ),
        createLegacyLeaf(
          'Survey Point Filter Management',
          '/legacy/admin/survey-filters',
          '/Admin/SurveyPointFilterManagement/Index',
          adminOnly,
          true
        ),
        // Leftovers at bottom
        createLegacyLeaf('Field Data', '/legacy/field-data', '/FieldData/Index', [], true)
      ]
    },

    // 12. Quick Links
    quickLinksRouteItem,

    // 13. Admin – leftovers (not in user's main categories)
    {
      text: 'Admin',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [AppRoles.Admin],
      layout: <Layout />,
      subItems: [
        {
          text: 'User Management',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.admin.userManagement,
          allowedRoles: userManagementRoles,
          async lazy() {
            const component = await import('../features/userAdmin/UserManagement.tsx');
            return { Component: component.default };
          }
        }
      ]
    }
  ];
}

export const TimesheetLockedOutNavItems: RouteItem[] = [
  createLegacyRoute(
    'Timesheet',
    '/legacy/timesheet-locked-out',
    '/TimesheetNew/Index',
    [AppRoles.TimesheetLockedOut],
    undefined,
    true
  ),
  createLegacyRoute('Field Data', '/legacy/field-data', '/FieldData/Index', [], undefined, true),
  quickLinksRouteItem,
  createLegacyLeaf('My Time Off Requests', '/legacy/time-off', '/TimeOffRequests/Index', timesheetRoles, true),
  createLegacyLeaf(
    'My Safety Report Tickets',
    '/legacy/timesheet/safety',
    '/DailySafetyTickets/Index',
    [AppRoles.PartyChief, AppRoles.Admin, AppRoles.SafetyManagement],
    true
  ),
  // Leftovers at bottom
  createLegacyLeaf('Timesheet', '/legacy/timesheet', '/TimesheetNew/Index', timesheetRoles, true),
  createLegacyLeaf(
    'Monthly Timesheet Invoice',
    '/legacy/timesheet/invoice',
    '/TimesheetNew/TimesheetInvoice',
    [AppRoles.Admin, AppRoles.PartyChief],
    true
  ),

  // 4. Purchases
  {
    text: 'Purchases',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    allowedRoles: [AppRoles.AdminJr, AppRoles.Admin, AppRoles.PartyChief, AppRoles.PurchaseOrderOnly, AppRoles.Manager],
    layout: <Layout />,
    subItems: [
      createLegacyLeaf(
        'PO System',
        '/legacy/po-system',
        '/PurchaseOrder/Index',
        [AppRoles.AdminJr, AppRoles.Admin, AppRoles.PartyChief, AppRoles.PurchaseOrderOnly],
        true
      ),
      createLegacyLeaf(
        'PO Report',
        '/legacy/reporting/po-report',
        '/PurchaseOrder/PurchaseOrderReport',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      ),
      createLegacyLeaf(
        'Vendor Purchases',
        '/legacy/accounting/vendor-purchases',
        '/Accounting/VendorPurchases',
        [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.AdminJr],
        true
      ),
      createLegacyLeaf(
        'Accommodations Report',
        '/legacy/reporting/accommodations',
        '/Reporting/AccomodationsReport',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      )
    ]
  },

  // 5. Management
  {
    text: 'Management',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    allowedRoles: [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager, AppRoles.QuickLinkManagement],
    layout: <Layout />,
    subItems: [
      createLegacyLeaf(
        'Client Contact Management',
        '/legacy/management/contacts',
        '/Admin/Company/Index',
        managementRoles,
        true
      ),
      createLegacyLeaf(
        'Crew Tracking',
        '/legacy/management/crew-tracking',
        '/CrewTracking/Index',
        managementRoles,
        true
      ),
      jobStatusReportRouteItem,
      createLegacyLeaf(
        'Quick Link Management',
        '/legacy/management/quick-links',
        '/DocumentLinkManagement/Index',
        [AppRoles.Admin, AppRoles.QuickLinkManagement],
        true
      ),
      createLegacyLeaf(
        'Custom Job Code Management',
        '/legacy/admin/custom-job-codes',
        '/CustomJobManagement/Index',
        adminOnly,
        true
      ),
      createLegacyLeaf(
        'Custom Job Management',
        '/legacy/admin/custom-jobs',
        '/CustomJobManagement/JobManagement',
        adminOnly,
        true
      ),
      dropdownApplicationManagementRouteItem,
      createLegacyLeaf('Job Locks', '/legacy/admin/job-locks', '/Admin/EditLock/Index', adminOnly, true),
      mailingListManagementRouteItem,
      createLegacyLeaf(
        'Email Group Management',
        '/legacy/admin/email-groups',
        '/Admin/EmailGroups/Index',
        adminOnly,
        true
      ),
      createLegacyLeaf(
        'Report Builder Management',
        '/legacy/admin/report-builder',
        '/ReportBuilder/Manage',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      ),
      createLegacyLeaf(
        'Job Report Builder',
        '/legacy/reporting/report-builder',
        '/ReportBuilder/Index',
        adminAndJr.concat([AppRoles.Manager]),
        true
      ),
      createLegacyLeaf(
        'Job Production Status Report',
        '/legacy/reporting/job-production',
        '/ProductionStatus/Index',
        [AppRoles.Admin, AppRoles.ProductionStatusReport],
        true
      ),
      createLegacyLeaf(
        'Legal Status Report',
        '/legacy/reporting/legal-status',
        '/ProductionStatus/LegalStatusReport',
        adminOnly,
        true
      )
    ]
  },

  // 6. Inventory
  {
    text: 'Inventory',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    allowedRoles: [
      AppRoles.Admin,
      AppRoles.AdminJr,
      AppRoles.AccountingAdmin,
      AppRoles.OfficeEquipmentManagement,
      AppRoles.Manager
    ],
    layout: <Layout />,
    subItems: [
      {
        text: 'Equipment History Report',
        authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
        path: ApplicationRoutes.admin.equipmentHistoryReport,
        allowedRoles: [AppRoles.Admin, AppRoles.AdminJr, AppRoles.AccountingAdmin, AppRoles.Manager],
        async lazy() {
          const m = await import('../features/v2/equipment/history/EquipmentHistoryReportPage.tsx');
          return { Component: m.default };
        }
      },
      createLegacyLeaf(
        'Equipment Owner Report',
        '/legacy/reporting/equipment-owner',
        '/Equipment/EquipmentOwnerReport?isFieldEquipment=true',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      )
    ]
  },

  // 7. Safety
  {
    text: 'Safety',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    allowedRoles: [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager, AppRoles.SafetyManagement],
    layout: <Layout />,
    subItems: [
      createLegacyLeaf(
        'Safety Ticket Management',
        '/legacy/admin/safety-tickets',
        '/Admin/DropDown/SafetyTicketManagement',
        adminAndJr,
        true
      ),
      createLegacyLeaf(
        'Daily Safety Report Tickets',
        '/legacy/reporting/daily-safety',
        '/DailySafetyTickets/Index',
        [AppRoles.Admin, AppRoles.SafetyManagement],
        true
      ),
      createLegacyLeaf(
        'Job KM Usage Report',
        '/legacy/reporting/job-km',
        '/EquipmentUsageReport/JobKmReport',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      ),
      createLegacyLeaf(
        'My Safety Report Tickets',
        '/legacy/timesheet/safety',
        '/DailySafetyTickets/Index',
        [AppRoles.PartyChief, AppRoles.Admin, AppRoles.SafetyManagement],
        true
      ),
      createLegacyLeaf(
        'Safety Ticket Report',
        '/legacy/reporting/safety-ticket-report',
        '/Reporting/SafetyTicketReport',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      ),
      createLegacyLeaf(
        'ISNetworld Safety Report',
        '/legacy/reporting/isnetworld',
        '/Reporting/IsNetworldSafetyReport',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      )
    ]
  },

  // 8. Payroll
  {
    text: 'Payroll',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    allowedRoles: [
      AppRoles.Admin,
      AppRoles.AdminJr,
      AppRoles.Manager,
      AppRoles.OfficeTimeApprover,
      AppRoles.SupervisorOfficeTimeApprover,
      AppRoles.FieldTimeApprover,
      AppRoles.ProductionStaff
    ],
    layout: <Layout />,
    subItems: [
      createLegacyLeaf(
        'Field Time Approvals',
        '/legacy/admin/field-time-approvals',
        '/TimesheetApproval/Index?isOfficeTime=false',
        [AppRoles.Admin, AppRoles.FieldTimeApprover],
        true
      ),
      createLegacyLeaf(
        'Office Time Approvals',
        '/legacy/admin/office-time-approvals',
        '/TimesheetApproval/Index?isOfficeTime=true',
        [AppRoles.Admin, AppRoles.OfficeTimeApprover, AppRoles.SupervisorOfficeTimeApprover],
        true
      ),
      createLegacyLeaf(
        'Time Off Approvals',
        '/legacy/admin/time-off-approvals',
        '/TimeOffApproval/Index',
        adminOnly,
        true
      ),
      createLegacyLeaf('Stat Holidays', '/legacy/admin/holidays', '/Admin/HolidayManagement/Index', adminOnly, true),
      createLegacyLeaf(
        'Biweekly Payroll Report',
        '/legacy/reporting/biweekly-payroll',
        '/Reporting/PayrollReport',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      ),
      createLegacyLeaf(
        'Monthly Payroll Report',
        '/legacy/reporting/monthly-payroll',
        '/Reporting/MonthlyPayrollReport',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      ),
      createLegacyLeaf(
        'Production Payroll Report',
        '/legacy/reporting/production-payroll',
        '/TimesheetNew/ProductionPayrollReport',
        [AppRoles.Admin, AppRoles.ProductionStaff],
        true
      ),
      createLegacyLeaf(
        'Timesheet Summary',
        '/legacy/reporting/timesheet-summary',
        '/TimesheetNew/TimesheetSummary',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      ),
      createLegacyLeaf(
        'Timesheet Summary (Field)',
        '/legacy/management/timesheet-summary',
        '/TimesheetNew/TimesheetSummary?fieldTimeOnly=true',
        managementRoles,
        true
      ),
      createLegacyLeaf(
        'Equipment Usage Report',
        '/legacy/reporting/equipment-usage',
        '/EquipmentUsageReport/Index',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      ),
      createLegacyLeaf(
        'Equipment Usage Charge Rates',
        '/legacy/accounting/equipment-rates',
        '/EquipmentRates/EquipmentUsageChargeRates',
        [AppRoles.Admin, AppRoles.AccountingAdmin],
        true
      ),
      createLegacyLeaf(
        'Employee Report',
        '/legacy/reporting/user-report',
        '/Admin/UserManagement/UserManagementReport',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      )
    ]
  },

  // 9. Invoicing
  {
    text: 'Invoicing',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    allowedRoles: [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.VendorAdmin, AppRoles.AdminJr],
    layout: <Layout />,
    subItems: [
      createLegacyLeaf(
        'Account Code Management',
        '/legacy/accounting/codes',
        '/InvoiceCodeManagement/Index',
        [AppRoles.AccountingAdmin],
        true
      ),
      createLegacyLeaf(
        'Approved Invoice Report',
        '/legacy/accounting/approved-invoices',
        '/InvoiceReport/ApprovedInvoiceReport',
        [AppRoles.Admin, AppRoles.AccountingAdmin],
        true
      ),
      createLegacyLeaf(
        'Change Compass Address',
        '/legacy/accounting/address',
        '/CompassAddress/Index',
        [AppRoles.Admin, AppRoles.AccountingAdmin],
        true
      ),
      createLegacyLeaf(
        'Invoice Report',
        '/legacy/reporting/invoice-report',
        '/InvoiceReport/Index',
        [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
        true
      ),
      createLegacyLeaf(
        'Daily Field Ratesheet Management',
        '/legacy/admin/daily-field-rates',
        '/DailyFieldRateManagement/Index',
        adminOnly,
        true
      ),
      createLegacyLeaf(
        'Job Ratesheet Management',
        '/legacy/admin/job-ratesheets',
        '/Admin/RateSheetManagement/Index',
        adminOnly,
        true
      ),
      createLegacyLeaf(
        'Invoice Rules Engine',
        '/legacy/admin/invoice-rules',
        '/InvoiceRulesEngine/Index',
        adminOnly,
        true
      ),
      createLegacyLeaf(
        'Invoice Approval',
        '/legacy/accounting/invoice-approval',
        '/Accounting/Invoices',
        [AppRoles.Admin, AppRoles.AccountingAdmin],
        true
      ),
      createLegacyLeaf(
        'Invoice Number Report',
        '/legacy/accounting/invoice-numbers',
        '/Accounting/InvoiceNumberReport',
        [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.VendorAdmin],
        true
      ),
      createLegacyLeaf(
        'Charge Out Ratio Report',
        '/legacy/accounting/charge-out',
        '/AdminReport/ChargeOutRatioReport',
        [AppRoles.AccountingAdmin],
        true
      ),
      createLegacyLeaf(
        'Sales Report',
        '/legacy/accounting/sales',
        '/Reporting/SalesReport',
        [AppRoles.Admin, AppRoles.AccountingAdmin],
        true
      ),
      createLegacyLeaf(
        'View Invoices',
        '/legacy/accounting/view-invoices',
        '/Accounting/Invoices?isApprovalMode=false',
        [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.AdminJr],
        true
      )
    ]
  },

  // 10. Field Tools – user list first, then leftovers at bottom
  {
    text: 'Field Tools',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    allowedRoles: [AppRoles.Admin, AppRoles.Manager, AppRoles.PartyChief, AppRoles.Gis],
    layout: <Layout />,
    subItems: [
      createLegacyLeaf(
        'Survey Points',
        '/legacy/survey-points',
        '/SurveyPoint/Index',
        [AppRoles.Admin, AppRoles.Manager, AppRoles.PartyChief],
        true
      ),
      createLegacyLeaf(
        'Survey Point Filter Management',
        '/legacy/admin/survey-filters',
        '/Admin/SurveyPointFilterManagement/Index',
        [AppRoles.Admin, AppRoles.Gis],
        true
      ),
      // Leftovers at bottom
      createLegacyLeaf('Field Data', '/legacy/field-data', '/FieldData/Index', [], true)
    ]
  },

  // 11. Quick Links
  createLegacyRoute('Quick Links', '/legacy/quick-links', '/DocumentLink/Index', [], undefined, true),

  // 12. Admin – leftovers (not in user's main categories)
  {
    text: 'Admin',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    allowedRoles: [
      AppRoles.Admin,
      AppRoles.UserAdmin,
      AppRoles.Manager,
      AppRoles.ProjectManager,
      AppRoles.OfficeTimeApprover,
      AppRoles.SupervisorOfficeTimeApprover,
      AppRoles.FieldTimeApprover,
      AppRoles.OfficeEquipmentManagement,
      AppRoles.AdminJr,
      AppRoles.AccountingAdmin,
      AppRoles.Gis
    ],
    layout: <Layout />,
    subItems: [
      {
        text: 'User Management',
        authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
        path: ApplicationRoutes.admin.userManagement,
        allowedRoles: userManagementRoles,
        async lazy() {
          const component = await import('../features/userAdmin/UserManagement.tsx');
          return { Component: component.default };
        }
      },
      {
        text: 'Company Management',
        authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
        path: ApplicationRoutes.admin.companyManagement,
        allowedRoles: [AppRoles.Admin, AppRoles.AdminJr, AppRoles.ProjectManager, AppRoles.Manager],
        async lazy() {
          const component = await import('../features/v2/company/overview/CompanyManagement.tsx');
          return { Component: component.default };
        }
      },
      {
        text: 'Rate Sheet Management',
        authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
        path: ApplicationRoutes.invoicing.rateSheetManagement,
        allowedRoles: [AppRoles.Admin],
        async lazy() {
          const component = await import('../features/v2/rateSheets/overview/RateSheetManagement.tsx');
          return { Component: component.default };
        }
      }
    ]
  }
];

// Non-nav routes (accessible via links but not shown in navigation)
export const LegacyHiddenRoutes: RouteItem[] = [
  ...V2InvoicingHiddenRoutes,
  V2InvoiceCreateRouteItem,
  createForcedLegacyRoute(
    'Create Invoice (Legacy)',
    ApplicationRoutes.invoicing.legacyCreate,
    '/Invoicing/JobInvoice',
    legacyInvoiceCreationRoles,
    undefined,
    true
  ),
  {
    text: 'Timesheet Summary (Admin Legacy Path)',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: ApplicationRoutes.reporting.timesheetSummaryAdmin,
    allowedRoles: [AppRoles.AdminJr],
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/reporting/timesheetSummary/TimesheetSummaryReportPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Invoice Editor',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: ApplicationRoutes.invoicing.invoiceEditor,
    allowedRoles: v2InvoicingRoles,
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/invoicing/invoiceEditor/InvoiceEditorPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Invoice Master',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: ApplicationRoutes.invoicing.invoiceMaster,
    allowedRoles: v2InvoicingRoles,
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/invoicing/invoiceEditor/InvoiceMasterViewPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Invoice Print',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: ApplicationRoutes.invoicing.invoicePrint,
    allowedRoles: v2InvoicingRoles,
    layout: <Outlet />,
    async lazy() {
      const m = await import('../features/v2/invoicing/invoiceEditor/InvoicePrintPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Invoice Number Report (Legacy Path)',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: '/legacy/accounting/invoice-numbers',
    allowedRoles: [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.VendorAdmin],
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/invoicing/invoiceNumberReport/InvoiceNumberReportPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Daily Safety Combined PDF',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: ApplicationRoutes.safety.dailySafetyCombinedPdf,
    allowedRoles: [AppRoles.Admin, AppRoles.SafetyManagement],
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/safety/dailySafety/DailySafetyCombinedPdfRoute.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Safety Ticket Report (Legacy Path)',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: '/legacy/reporting/safety-ticket-report',
    allowedRoles: reportViewerRoles,
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/safety/safetyTicketReport/SafetyTicketReportPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Equipment History Detail',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: ApplicationRoutes.admin.equipmentHistoryReportDetail,
    allowedRoles: [AppRoles.Admin, AppRoles.AdminJr, AppRoles.AccountingAdmin],
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/equipment/history/EquipmentHistoryDetailPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Edit Job Legacy Redirect',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: '/EditJob',
    additionalPaths: ['/EditJob/Index'],
    allowedRoles: editJobRoles,
    layout: <Layout />,
    async lazy() {
      return { Component: LegacyEditJobRedirect };
    }
  },
  createLegacyRoute('Edit Job', '/legacy/editjob', '/EditJob', editJobRoles, undefined, true),
  createLegacyRoute(
    'Admin Vendor Purchases',
    '/legacy/admin/vendor-purchases',
    '/Accounting/VendorPurchases',
    [AppRoles.AdminJr],
    undefined,
    true
  ),
  createForcedLegacyRoute(
    'Edit Job Ratesheet',
    '/legacy/admin/ratesheetmanagement/editratesheet',
    '/Admin/RateSheetManagement/EditRateSheet',
    [AppRoles.Admin],
    undefined,
    true
  ),
  createLegacyRoute('Edit Job Legal', '/legacy/edit-job/legal', '/EditJob/Legal', editJobLegalRoles, undefined, true),
  createLegacyRoute(
    'Job Costing',
    '/legacy/costing',
    '/Costing/Index',
    [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.ProjectManager, AppRoles.Manager],
    undefined,
    true
  ),
  {
    text: 'Job Time Summary',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: ApplicationRoutes.reporting.jobActivity,
    allowedRoles: [
      AppRoles.AdminJr,
      AppRoles.Admin,
      AppRoles.Checker,
      AppRoles.Drafter,
      AppRoles.ProjectManager,
      AppRoles.Manager,
      AppRoles.PartyChief,
      AppRoles.SurveyAssistant,
      AppRoles.VendorAdmin
    ],
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/jobs/timeSummary/JobTimeSummaryReportPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Daily Field Rate',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: '/legacy/invoicing/daily-field-rate',
    allowedRoles: [AppRoles.Admin, AppRoles.PartyChief],
    layout: <Layout />,
    lazy: async () => {
      const { DailyFieldRateRoute } = await import('../components/invoicing/DailyFieldRateRoute');
      return { Component: DailyFieldRateRoute };
    }
  },
  createForcedLegacyRoute(
    'View Job Invoices',
    '/legacy/invoicing/view-invoices',
    '/Invoicing/ViewJobInvoices',
    [AppRoles.Admin, AppRoles.AdminJr, AppRoles.ProjectManager, AppRoles.Manager],
    undefined,
    true
  ),
  createForcedLegacyRoute(
    'Print Invoice',
    '/legacy/invoicing/print',
    '/Invoicing/PrintJobInvoice',
    [AppRoles.Admin, AppRoles.AdminJr, AppRoles.ProjectManager, AppRoles.Manager, AppRoles.AccountingAdmin],
    undefined,
    true
  ),
  createLegacyRoute('Job History', '/legacy/job-history', '/JobHistory/Index', [AppRoles.Admin], undefined, true)
];
